/**
 * Dates are stored ONCE, as ISO-8601 calendar dates in AD ("2026-08-18").
 * Bikram Sambat is a *display* concern, and only for an organisation whose localization pack uses
 * it (ADR-0033). The arithmetic is `@rigo/nepali-calendar`, through `lib/calendar.ts`.
 */
export type IsoDate = string; // "YYYY-MM-DD"

const SHORT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const LONG = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const DT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Asia/Kathmandu",
});

function parse(iso: IsoDate): Date {
  // Treat as UTC midnight so formatting is timezone-independent.
  return new Date(iso + "T00:00:00Z");
}

/** "18 Aug 2026" */
export function formatDate(iso: IsoDate | null | undefined): string {
  if (!iso) return "—";
  return SHORT.format(parse(iso));
}

/** "18 August 2026" */
export function formatDateLong(iso: IsoDate | null | undefined): string {
  if (!iso) return "—";
  return LONG.format(parse(iso));
}

/** "20 Aug 2026, 11:04" in Asia/Kathmandu */
export function formatDateTime(isoInstant: string | null | undefined): string {
  if (!isoInstant) return "—";
  return DT.format(new Date(isoInstant));
}

export function today(): IsoDate {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(iso: IsoDate, days: number): IsoDate {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000);
}

/** "Due in 2d" / "Overdue 5d" / "Due today" */
export function dueLabel(due: IsoDate, ref: IsoDate = today()): string {
  const d = daysBetween(ref, due);
  if (d === 0) return "Due today";
  return d > 0 ? `Due in ${d}d` : `Overdue ${-d}d`;
}

/** "Idle 9d" style relative label for queues. */
export function waitingLabel(sinceIso: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(sinceIso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
